/** OpenVPN servers admin — tab on Servers page (secrets in DB, no install). */
import { useCallback, useEffect, useState } from 'react';
import { useToast } from './ToastProvider';
import { api } from '../services/api';
import type { OvpnServerHealth, OvpnServerListItem } from '../services/types';
import { notifyApiResult } from '../utils/notify';

type EditForm = {
  enabled: boolean;
  remote_host: string;
  remote_port: number;
  proto: string;
  ssh_host: string;
  ssh_port: number;
  ssh_user: string;
  ssh_password: string;
  ssh_password_env: string;
  max_keys_per_user: number;
};

function toForm(s: OvpnServerListItem): EditForm {
  return {
    enabled: s.enabled,
    remote_host: s.remote_host || '',
    remote_port: s.remote_port || 8443,
    proto: s.proto || 'tcp',
    ssh_host: s.ssh_host || '',
    ssh_port: s.ssh_port || 22,
    ssh_user: s.ssh_user || 'root',
    ssh_password: '',
    ssh_password_env: s.ssh_password_env || '',
    max_keys_per_user: s.max_keys_per_user || 3,
  };
}

export function OvpnServersPanel({ canWrite }: { canWrite: boolean }) {
  const toast = useToast();
  const [servers, setServers] = useState<OvpnServerListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<EditForm | null>(null);
  const [busy, setBusy] = useState(false);
  const [healthMap, setHealthMap] = useState<Record<string, OvpnServerHealth>>({});

  const load = useCallback(
    async (background = false) => {
      if (!background) setLoading(true);
      setError(null);
      const res = await api.listOvpnServers();
      if (res.data) setServers(res.data);
      else if (res.error) {
        setError(res.error.message);
        if (!background) toast.error(res.error.message);
      }
      if (!background) setLoading(false);
    },
    [toast],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const openEdit = (s: OvpnServerListItem) => {
    setEditing(s.server_code);
    setForm(toForm(s));
  };

  const save = async () => {
    if (!editing || !form) return;
    setBusy(true);
    const payload: Record<string, unknown> = {
      enabled: form.enabled,
      remote_host: form.remote_host.trim(),
      remote_port: Number(form.remote_port) || 8443,
      proto: form.proto.trim().toLowerCase(),
      ssh_host: form.ssh_host.trim(),
      ssh_port: Number(form.ssh_port) || 22,
      ssh_user: form.ssh_user.trim(),
      ssh_password_env: form.ssh_password_env.trim() || null,
      max_keys_per_user: Number(form.max_keys_per_user) || 3,
    };
    if (form.ssh_password.trim()) payload.ssh_password = form.ssh_password;
    const res = await api.updateOvpnServer(editing, payload);
    if (notifyApiResult(toast, res, () => `OVPN ${editing} обновлён`)) {
      setEditing(null);
      setForm(null);
      await load(true);
    }
    setBusy(false);
  };

  const importSecrets = async () => {
    if (
      !window.confirm(
        'Импортировать OVPN_*_SSH_PASSWORD из .env в БД (Fernet)?\n' +
          'Серверы, где секрет уже в БД, будут пропущены.',
      )
    ) {
      return;
    }
    setBusy(true);
    const res = await api.importOvpnSecrets();
    if (
      notifyApiResult(
        toast,
        res,
        (d) =>
          `Импорт: +${d.imported}, пропущено ${d.skipped}, нет в env ${d.missing_env}`,
      )
    ) {
      await load(true);
    }
    setBusy(false);
  };

  const checkHealth = async (code: string) => {
    setBusy(true);
    const res = await api.getOvpnServerHealth(code);
    if (res.data) {
      setHealthMap((prev) => ({ ...prev, [code]: res.data! }));
      if (res.data.ssh_ok && res.data.service_ok) toast.success(`${code}: SSH + OpenVPN OK`);
      else if (res.data.ssh_ok) toast.error(`${code}: SSH OK, service=${res.data.detail || 'inactive'}`);
      else toast.error(`${code}: ${res.data.error || 'SSH failed'}`);
    } else if (res.error) toast.error(res.error.message);
    setBusy(false);
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        {canWrite && (
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void importSecrets()}>
            Импорт паролей из env
          </button>
        )}
        <button type="button" className="btn btn-ghost" disabled={loading} onClick={() => void load()}>
          Обновить
        </button>
      </div>

      {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}
      {loading && servers.length === 0 && <p style={{ color: 'var(--text-muted)' }}>Загрузка…</p>}
      {!loading && servers.length === 0 && (
        <p style={{ color: 'var(--text-muted)' }}>В таблице ovpn_servers пока нет записей.</p>
      )}

      {servers.length > 0 && (
        <div className="table-wrap" style={{ marginBottom: '1rem' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Remote</th>
                <th>SSH</th>
                <th>Secret</th>
                <th>Enabled</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {servers.map((s) => {
                const h = healthMap[s.server_code];
                return (
                  <tr key={s.server_code}>
                    <td>
                      <code>{s.server_code}</code>
                    </td>
                    <td>
                      {s.remote_host}:{s.remote_port}/{s.proto}
                    </td>
                    <td>
                      {s.ssh_user}@{s.ssh_host}:{s.ssh_port}
                    </td>
                    <td>
                      <span title={s.ssh_password_env || undefined}>{s.secret_source}</span>
                      {h && (
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                          SSH:{h.ssh_ok ? '✓' : '✗'} svc:{h.service_ok ? '✓' : '✗'}
                        </div>
                      )}
                    </td>
                    <td>{s.enabled ? 'да' : 'нет'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          className="btn btn-ghost"
                          disabled={busy}
                          onClick={() => void checkHealth(s.server_code)}
                        >
                          Health
                        </button>
                        {canWrite && (
                          <button type="button" className="btn btn-ghost" onClick={() => openEdit(s)}>
                            Изменить
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && form && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>OVPN · {editing}</h3>
          <div className="form-grid" style={{ display: 'grid', gap: '0.75rem', maxWidth: 520 }}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => setForm((f) => (f ? { ...f, enabled: e.target.checked } : f))}
              />
              <span>Enabled</span>
            </label>
            {(
              [
                ['remote_host', 'Remote host'],
                ['remote_port', 'Remote port'],
                ['proto', 'Proto (udp/tcp)'],
                ['ssh_host', 'SSH host'],
                ['ssh_port', 'SSH port'],
                ['ssh_user', 'SSH user'],
                ['ssh_password', 'SSH password (пусто = не менять)'],
                ['ssh_password_env', 'Env fallback name (optional)'],
                ['max_keys_per_user', 'Max keys / user'],
              ] as const
            ).map(([key, label]) => (
              <label key={key} style={{ display: 'grid', gap: 4 }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>{label}</span>
                <input
                  className="input"
                  type={
                    key === 'ssh_password'
                      ? 'password'
                      : key === 'remote_port' || key === 'ssh_port' || key === 'max_keys_per_user'
                        ? 'number'
                        : 'text'
                  }
                  value={String(form[key])}
                  onChange={(e) =>
                    setForm((f) =>
                      f
                        ? {
                            ...f,
                            [key]:
                              key === 'remote_port' || key === 'ssh_port' || key === 'max_keys_per_user'
                                ? Number(e.target.value)
                                : e.target.value,
                          }
                        : f,
                    )
                  }
                />
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void save()}>
              Сохранить
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                setEditing(null);
                setForm(null);
              }}
            >
              Отмена
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
